<script lang="ts">
  import { BundleProcessor } from '@legere/bundle-processor';
  import { FB2Converter, type ConversionProgress } from '@legere/fb2-converter';
  import { getBookStorage } from '../lib/storage';
  import { books } from '../stores/books';
  import type { Book } from '../../../../packages/storage-manager/src/index';

  let uploading = false;
  let error: string | null = null;
  let successMessage: string | null = null;
  let conversionProgress: ConversionProgress | null = null;

  const bundleProcessor = new BundleProcessor();
  const fb2Converter = new FB2Converter();

  async function handleFileUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    uploading = true;
    error = null;
    successMessage = null;

    try {
      const fileName = file.name;
      const isMdZip = fileName.endsWith('.md.zip');
      const isMd = fileName.endsWith('.md');
      const isFb2Zip = fileName.endsWith('.fb2.zip');
      const isFb2 = fileName.endsWith('.fb2');

      if (!isMdZip && !isMd && !isFb2Zip && !isFb2) {
        throw new Error('Please upload a .md, .md.zip, .fb2, or .fb2.zip file');
      }

      const arrayBuffer = await file.arrayBuffer();
      let content: string;
      let title: string;
      let format: 'md' | 'md.zip' = 'md';
      let bundle: any = undefined;
      let metadata: any = {
        fileSize: arrayBuffer.byteLength,
        language: 'en',
      };

      if (isFb2 || isFb2Zip) {
        // Convert FB2 to MD.zip with progress tracking
        const converted = await fb2Converter.convert(file, (progress) => {
          conversionProgress = progress;
        });

        // Create MD.zip bundle with progress tracking
        const mdZipBuffer = await fb2Converter.createMDZipBundle(converted, (progress) => {
          conversionProgress = progress;
        });

        // Extract bundle data
        const extractedBundle = await bundleProcessor.extract(mdZipBuffer);
        content = extractedBundle.readme;
        bundle = extractedBundle;
        title = converted.title;
        format = 'md.zip';

        // Add FB2 metadata
        metadata = {
          ...metadata,
          author: converted.metadata.author,
          genre: converted.metadata.genre,
          language: converted.metadata.lang || 'en',
          date: converted.metadata.date,
          originalFormat: isFb2Zip ? 'fb2.zip' : 'fb2'
        };
      } else if (isMdZip) {
        // Process .md.zip bundle
        const validation = await bundleProcessor.validate(arrayBuffer);

        if (!validation.valid) {
          throw new Error(validation.errors[0]?.message || 'Invalid bundle');
        }

        const extractedBundle = await bundleProcessor.extract(arrayBuffer);
        content = extractedBundle.readme;
        bundle = extractedBundle;
        title = fileName.replace('.md.zip', '');
        format = 'md.zip';
      } else {
        // Process plain .md file
        const decoder = new TextDecoder();
        content = decoder.decode(arrayBuffer);
        title = fileName.replace('.md', '');
        format = 'md';
      }

      // Create book object
      const book: Book = {
        id: crypto.randomUUID(),
        title,
        format,
        content,
        bundle,
        metadata,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save to storage
      const storage = getBookStorage();
      await storage.addBook(book);

      // Update books store
      const allBooks = await storage.getAllBooks();
      books.set(allBooks);

      successMessage = `"${title}" added successfully!`;

      // Clear file input
      target.value = '';

    } catch (err) {
      error = err instanceof Error ? err.message : 'Upload failed';
    } finally {
      uploading = false;
      conversionProgress = null;
    }
  }
</script>

<div class="upload-section mb-8">
  <label
    for="file-upload"
    class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
  >
    <div class="flex flex-col items-center justify-center pt-5 pb-6">
      <svg class="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      {#if uploading}
        {#if conversionProgress}
          <p class="text-sm text-gray-500">{conversionProgress.message}</p>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
            <div
              class="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style="width: {conversionProgress.progress}%"
            ></div>
          </div>
        {:else}
          <p class="text-sm text-gray-500">Uploading...</p>
        {/if}
      {:else}
        <p class="mb-2 text-sm text-gray-500">
          <span class="font-semibold">Click to upload</span> or drag and drop
        </p>
        <p class="text-xs text-gray-500">.md, .md.zip, .fb2, or .fb2.zip files</p>
      {/if}
    </div>
    <input
      id="file-upload"
      type="file"
      class="hidden"
      accept=".md,.zip,.fb2"
      on:change={handleFileUpload}
      disabled={uploading}
    />
  </label>

  {#if error}
    <div class="mt-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
      {error}
    </div>
  {/if}

  {#if successMessage}
    <div class="mt-4 p-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200 rounded">
      {successMessage}
    </div>
  {/if}
</div>
